import { useState } from "react";
import { Calculator, TrendingUp, FileCheck2, ScrollText, LineChart, Ruler } from "lucide-react";
import { formatINR } from "../lib/format";

const TABS = [
  { id: "emi", label: "EMI", icon: Calculator },
  { id: "afford", label: "Affordability", icon: TrendingUp },
  { id: "stamp", label: "Stamp Duty", icon: FileCheck2 },
  { id: "reg", label: "Registration", icon: ScrollText },
  { id: "yield", label: "Rental Yield", icon: LineChart },
  { id: "area", label: "Area Converter", icon: Ruler },
];

export default function Calculators() {
  const [tab, setTab] = useState("emi");
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
      <div className="text-center max-w-2xl mx-auto">
        <span className="text-sm font-bold uppercase tracking-wider text-pbs-purple">Smart Tools</span>
        <h1 className="mt-1.5 text-3xl sm:text-4xl font-extrabold text-pbs-dark">Property Calculators</h1>
        <p className="mt-2 text-pbs-grey">Interactive tools to plan your purchase with confidence.</p>
      </div>

      <div className="mt-8 flex gap-2 overflow-x-auto no-scrollbar justify-center">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`calc-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap border transition-colors ${tab === t.id ? "pbs-gradient text-white border-transparent" : "bg-white border-pbs-line text-pbs-grey hover:border-pbs-purple"}`}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      <div className="mt-8 max-w-3xl mx-auto">
        {tab === "emi" && <EMI />}
        {tab === "afford" && <Afford />}
        {tab === "stamp" && <Stamp />}
        {tab === "reg" && <Registration />}
        {tab === "yield" && <Yield />}
        {tab === "area" && <AreaConverter />}
      </div>
    </div>
  );
}

function Shell({ children, result }) {
  return (
    <div className="grid md:grid-cols-5 gap-6">
      <div className="md:col-span-3 bg-white rounded-2xl border border-pbs-line p-6 pbs-shadow space-y-5">{children}</div>
      <div className="md:col-span-2 rounded-2xl pbs-gradient p-6 text-white flex flex-col justify-center">{result}</div>
    </div>
  );
}
const Slider = ({ label, value, onChange, min, max, step, format }) => (
  <div>
    <div className="flex justify-between items-center mb-1.5">
      <span className="text-sm font-semibold text-pbs-dark">{label}</span>
      <span className="text-sm font-bold text-pbs-purple font-mono">{format ? format(value) : value}</span>
    </div>
    <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-pbs-purple" />
  </div>
);
const Big = ({ label, value, sub }) => (
  <div className="text-center"><p className="text-white/70 text-sm">{label}</p><p className="text-3xl sm:text-4xl font-extrabold font-mono mt-1">{value}</p>{sub && <p className="text-white/70 text-xs mt-1">{sub}</p>}</div>
);

function EMI() {
  const [amount, setAmount] = useState(5000000);
  const [rate, setRate] = useState(8.5);
  const [years, setYears] = useState(20);
  const r = rate / 12 / 100, n = years * 12;
  const emi = r === 0 ? amount / n : (amount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  const total = emi * n, interest = total - amount;
  return (
    <Shell result={<div className="space-y-5"><Big label="Monthly EMI" value={formatINR(Math.round(emi))} /><div className="grid grid-cols-2 gap-3 text-center text-sm"><div><p className="text-white/70">Interest</p><p className="font-bold font-mono">{formatINR(Math.round(interest))}</p></div><div><p className="text-white/70">Total</p><p className="font-bold font-mono">{formatINR(Math.round(total))}</p></div></div></div>}>
      <input data-testid="emi-calculator-input-amount" type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} className="w-full border border-pbs-line rounded-lg px-3 py-2.5 font-mono text-pbs-dark" />
      <Slider label="Loan Amount" value={amount} onChange={setAmount} min={500000} max={100000000} step={100000} format={formatINR} />
      <Slider label="Interest Rate (% p.a.)" value={rate} onChange={setRate} min={5} max={15} step={0.1} format={(v) => `${v}%`} />
      <Slider label="Tenure (years)" value={years} onChange={setYears} min={1} max={30} step={1} format={(v) => `${v} yr`} />
    </Shell>
  );
}
function Afford() {
  const [income, setIncome] = useState(150000);
  const [obligations, setObligations] = useState(20000);
  const [rate, setRate] = useState(8.5);
  const [years, setYears] = useState(20);
  const available = Math.max(0, income * 0.5 - obligations);
  const r = rate / 12 / 100, n = years * 12;
  const loan = available * (Math.pow(1 + r, n) - 1) / (r * Math.pow(1 + r, n));
  const budget = loan * 1.25;
  return (
    <Shell result={<div className="space-y-5"><Big label="You can afford up to" value={formatINR(Math.round(budget))} sub="including ~20% down payment" /><div className="text-center text-sm"><p className="text-white/70">Eligible Loan</p><p className="font-bold font-mono">{formatINR(Math.round(loan))}</p></div></div>}>
      <Slider label="Monthly Income" value={income} onChange={setIncome} min={25000} max={1000000} step={5000} format={formatINR} />
      <Slider label="Monthly Obligations" value={obligations} onChange={setObligations} min={0} max={300000} step={2500} format={formatINR} />
      <Slider label="Interest Rate (% p.a.)" value={rate} onChange={setRate} min={5} max={15} step={0.1} format={(v) => `${v}%`} />
      <Slider label="Tenure (years)" value={years} onChange={setYears} min={1} max={30} step={1} format={(v) => `${v} yr`} />
    </Shell>
  );
}
function Stamp() {
  const [price, setPrice] = useState(8000000);
  const [rate, setRate] = useState(6);
  const duty = price * rate / 100;
  return (
    <Shell result={<div className="space-y-5"><Big label="Stamp Duty" value={formatINR(Math.round(duty))} sub={`${rate}% of property value`} /><div className="text-center text-sm"><p className="text-white/70">Total Outflow</p><p className="font-bold font-mono">{formatINR(Math.round(price + duty))}</p></div></div>}>
      <Slider label="Property Value" value={price} onChange={setPrice} min={1000000} max={100000000} step={100000} format={formatINR} />
      <Slider label="Stamp Duty Rate (%)" value={rate} onChange={setRate} min={3} max={8} step={0.5} format={(v) => `${v}%`} />
      <p className="text-xs text-pbs-grey">Rates vary by state and buyer category. This is an indicative estimate.</p>
    </Shell>
  );
}
function Registration() {
  const [price, setPrice] = useState(8000000);
  const [rate, setRate] = useState(1);
  const fee = Math.min(price * rate / 100, 3000000);
  return (
    <Shell result={<div className="space-y-5"><Big label="Registration Charges" value={formatINR(Math.round(fee))} sub={`${rate}% of property value`} /></div>}>
      <Slider label="Property Value" value={price} onChange={setPrice} min={1000000} max={100000000} step={100000} format={formatINR} />
      <Slider label="Registration Rate (%)" value={rate} onChange={setRate} min={0.5} max={2} step={0.25} format={(v) => `${v}%`} />
      <p className="text-xs text-pbs-grey">Typically ~1% of property value, subject to state caps.</p>
    </Shell>
  );
}
function Yield() {
  const [price, setPrice] = useState(12000000);
  const [rent, setRent] = useState(35000);
  const annual = rent * 12;
  const grossYield = (annual / price) * 100;
  const netYield = grossYield * 0.8;
  return (
    <Shell result={<div className="space-y-5"><Big label="Gross Rental Yield" value={`${grossYield.toFixed(2)}%`} /><div className="grid grid-cols-2 gap-3 text-center text-sm"><div><p className="text-white/70">Net Yield*</p><p className="font-bold font-mono">{netYield.toFixed(2)}%</p></div><div><p className="text-white/70">Annual Rent</p><p className="font-bold font-mono">{formatINR(annual)}</p></div></div></div>}>
      <Slider label="Property Value" value={price} onChange={setPrice} min={1000000} max={100000000} step={100000} format={formatINR} />
      <Slider label="Monthly Rent" value={rent} onChange={setRent} min={5000} max={500000} step={1000} format={formatINR} />
      <p className="text-xs text-pbs-grey">*Net yield assumes ~20% towards maintenance, taxes and vacancy.</p>
    </Shell>
  );
}
function AreaConverter() {
  const UNITS = { "Sq. Feet": 1, "Sq. Meter": 10.7639, "Sq. Yard": 9, "Acre": 43560, "Hectare": 107639, "Bigha (std)": 27000, "Gaj": 9 };
  const [value, setValue] = useState(1000);
  const [from, setFrom] = useState("Sq. Feet");
  const sqft = value * UNITS[from];
  return (
    <Shell result={<div className="space-y-2.5 w-full">{Object.entries(UNITS).map(([u, f]) => (<div key={u} className="flex justify-between text-sm border-b border-white/15 pb-1.5"><span className="text-white/80">{u}</span><span className="font-bold font-mono">{(sqft / f).toLocaleString("en-IN", { maximumFractionDigits: 3 })}</span></div>))}</div>}>
      <div>
        <label className="text-sm font-semibold text-pbs-dark">Value</label>
        <input data-testid="area-input" type="number" value={value} onChange={(e) => setValue(Number(e.target.value))} className="w-full mt-1.5 border border-pbs-line rounded-lg px-3 py-2.5 font-mono text-pbs-dark" />
      </div>
      <div>
        <label className="text-sm font-semibold text-pbs-dark">From Unit</label>
        <select data-testid="area-from" value={from} onChange={(e) => setFrom(e.target.value)} className="w-full mt-1.5 border border-pbs-line rounded-lg px-3 py-2.5 text-pbs-dark bg-white">
          {Object.keys(UNITS).map((u) => <option key={u}>{u}</option>)}
        </select>
      </div>
    </Shell>
  );
}
